/**
 * The definition popover.
 *
 * One sentence saying what a number means, attached to the thing that shows the number. It
 * opens on hover and on keyboard focus, closes on Escape, and is a real `role="tooltip"`
 * wired with `aria-describedby` rather than a `title` attribute, which no screen reader
 * announces reliably and no keyboard user can reach at all.
 *
 * The tooltip element is always in the DOM so `aria-describedby` never dangles: assistive
 * technology reads the description whether or not the visual bubble is showing.
 *
 * A missing definition renders the trigger alone. The legend is the source of these strings
 * and an id the server has not published yet is a gap in the legend, not a crash here.
 */
import {
  cloneElement,
  isValidElement,
  useCallback,
  useId,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

export interface DefinitionPopoverProps {
  /** The definition sentence, from the /legend response. Null renders the trigger bare. */
  definition: string | null | undefined;
  /** What the definition describes. A single element gets the aria-describedby directly. */
  children: ReactNode;
  /** Stable id root, so the tooltip id is predictable in a test. Generated otherwise. */
  id?: string;
  /** Extra lines under the sentence, for example the unit and the basis. */
  detail?: string | null;
  placement?: 'below' | 'above';
}

export function DefinitionPopover({
  definition,
  children,
  id,
  detail,
  placement = 'below',
}: DefinitionPopoverProps) {
  const generated = useId();
  const tooltipId = `${id ?? generated}-definition`;
  const [open, setOpen] = useState(false);

  const onKeyDown = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Escape') {
      // Stop here so Escape closes the popover without also closing the drawer behind it.
      event.stopPropagation();
      setOpen(false);
    }
  }, []);

  if (!definition) return <>{children}</>;

  const trigger = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, {
        'aria-describedby': tooltipId,
      })
    : children;

  return (
    <span
      className="o-def"
      data-open={open ? 'true' : 'false'}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={onKeyDown}
      {...(isValidElement(children) ? {} : { 'aria-describedby': tooltipId })}
    >
      {trigger}
      <span
        role="tooltip"
        id={tooltipId}
        className={`o-def__bubble o-def__bubble--${placement}`}
      >
        <span className="o-def__text">{definition}</span>
        {detail ? <span className="o-def__detail">{detail}</span> : null}
      </span>
    </span>
  );
}
